//@ts-check
import {getCurrentDateInTimezoneToUtc} from '@/helpers/dateUtils'
import {useFeatureFlagLanguages} from '@/hooks/useFeatureFlagLanguages'
import {getMandatoryEditableColumns} from '@/hooks/useProductListColumns'
import {bulkEditProducts} from '@/services/ProductsService/product'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {mapEditedValuesToFieldValues} from '@/utils/productListBulkEditMapper'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import {POSITIVE_INTEGER_COLUMN_KEYS, isPositiveInteger} from '../features/ProductList/ProductListTabContent/constants'

/**
 * Manages inline bulk-edit state for the v2 Product List table and calls the
 * `PATCH /v1/api/product/bulk` endpoint.
 *
 * UNIFORM semantics: editing a field on a the first row that's part of the current multi-row
 * selection mirrors that edit to every selected row's draft values, so Save sends one
 * UNIFORM request for the whole group. Edits on rows outside the selection (or when
 * only a single row is selected) stay PER_PRODUCT.
 *
 * @param {{ selectedRows: {productId: string}[], onSaved?: () => void, selectionResetKey?: string | null, clearSelection?: () => void }} params
 * `onSaved` runs after a save attempt refetches the table - saved rows may no longer match the active search, so
 * the caller can clear a selection that would otherwise point at rows no longer on screen. While editing,
 * `clearSelection` is called whenever `selectionResetKey` changes (a new sort/filter), so UNIFORM edits never
 * target selected rows that are no longer shown; a `null` key is ignored.
 */
export const useProductListBulkEdit = ({selectedRows, onSaved, selectionResetKey = null, clearSelection}) => {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const countryCode = useSelector(selectCountryCode)
  const languages = useFeatureFlagLanguages()
  const localizedLanguage = languages?.find(language => language.code !== 'en') ?? languages?.[0]
  const locale = localizedLanguage?.code || 'es_US'
  const [isEditMode, setIsEditMode] = useState(false)
  const [editedValues, setEditedValues] = useState(/** @type {Record<string, Record<string, any>>} */ ({}))

  /** @typedef {{ mode: 'UNIFORM' | 'PER_PRODUCT', productIds?: string[], fieldValues?: object, edits?: {productId: string, fieldValues: object}[] }} BulkEditPayload */

  const {mutateAsync: bulkEditMutateAsync, isPending: isSavingEdits} = useMutation({
    mutationFn: payload => bulkEditProducts(payload, {countryCode}),
  })

  const onEnterEditMode = useCallback(() => setIsEditMode(true), [])

  const previousSelectionResetKeyRef = useRef(selectionResetKey)
  useEffect(() => {
    if (selectionResetKey === null || previousSelectionResetKeyRef.current === selectionResetKey) return
    previousSelectionResetKeyRef.current = selectionResetKey
    if (isEditMode) clearSelection?.()
  }, [selectionResetKey, isEditMode, clearSelection])

  const onEditValueChange = useCallback(
    (productId, columnKey, value) => {
      const isPartOfMultiSelection = selectedRows.length > 1 && selectedRows.some(row => row.productId === productId)
      const targetIds = isPartOfMultiSelection ? selectedRows.map(row => row.productId) : [productId]

      setEditedValues(prev => {
        const next = {...prev}
        targetIds.forEach(id => {
          const currentEdits = next[id]

          if (columnKey === 'newSkuTag') {
            const isNewProduct = value === true || String(value).toUpperCase() === 'TRUE'
            next[id] = {
              ...currentEdits,
              [columnKey]: value,
              newSkuStartDate: isNewProduct ? getCurrentDateInTimezoneToUtc() : null,
              newSkuEndDate: isNewProduct ? getCurrentDateInTimezoneToUtc(90) : null,
            }
            return
          }

          next[id] = {...currentEdits, [columnKey]: value}
        })
        return next
      })
    },
    [selectedRows]
  )

  const onCancelEdit = useCallback(() => {
    setIsEditMode(false)
    setEditedValues({})
  }, [])

  // Blocks Save when a mandatory editable field (e.g. Display Name) was cleared out on any edited row.
  const mandatoryEditableColumns = useMemo(() => getMandatoryEditableColumns(countryCode), [countryCode])
  const missingRequiredFieldColumns = useMemo(() => {
    const rowsWithEdits = Object.values(editedValues)
    return mandatoryEditableColumns.filter(column =>
      rowsWithEdits.some(
        fields =>
          Object.prototype.hasOwnProperty.call(fields, column.columnKey) &&
          !String(fields[column.columnKey] ?? '').trim()
      )
    )
  }, [editedValues, mandatoryEditableColumns])
  const hasMissingRequiredFields = missingRequiredFieldColumns.length > 0

  const hasValidationErrors = useMemo(
    () =>
      Object.values(editedValues).some(fields =>
        POSITIVE_INTEGER_COLUMN_KEYS.some(
          columnKey => Object.prototype.hasOwnProperty.call(fields, columnKey) && !isPositiveInteger(fields[columnKey])
        )
      ),
    [editedValues]
  )

  const onSaveEdit = useCallback(async () => {
    const editedProductIds = Object.keys(editedValues)
    if (editedProductIds.length === 0 || hasMissingRequiredFields || hasValidationErrors) {
      if (editedProductIds.length === 0) setIsEditMode(false)
      return
    }

    const selectedIds = new Set(selectedRows.map(row => row.productId))
    const isUniformEdit = productId => selectedRows.length > 1 && selectedIds.has(productId)

    const uniformIds = editedProductIds.filter(isUniformEdit)
    const perProductIds = editedProductIds.filter(productId => !isUniformEdit(productId))

    // Sent one after the other (not in parallel) so the backend finishes persisting the first
    // batch before the second is applied.
    const results = []

    if (uniformIds.length > 0) {
      // All uniform rows carry identical edits (mirrored in onEditValueChange), so any one works as the source.
      results.push(
        await bulkEditMutateAsync({
          mode: 'UNIFORM',
          productIds: uniformIds,
          fieldValues: mapEditedValuesToFieldValues(editedValues[uniformIds[0]], locale),
        })
          .then(value => ({status: 'fulfilled', value}))
          .catch(reason => ({status: 'rejected', reason}))
      )
    }

    if (perProductIds.length > 0) {
      results.push(
        await bulkEditMutateAsync({
          mode: 'PER_PRODUCT',
          edits: perProductIds.map(productId => ({
            productId,
            fieldValues: mapEditedValuesToFieldValues(editedValues[productId], locale),
          })),
        })
          .then(value => ({status: 'fulfilled', value}))
          .catch(reason => ({status: 'rejected', reason}))
      )
    }
    const hasFailure = results.some(
      result => result.status === 'rejected' || /** @type {any} */ (result).value?.isSuccess === false
    )

    // Collect per-product error messages from the results array (e.g. VALIDATION_ERROR / BUSINESS_RULE_VIOLATION).
    // 400 responses are thrown by axios, so they land as 'rejected' with reason.data = the error body.
    const extractErrors = responseBody =>
      (responseBody?.results ?? []).flatMap(
        productResult => productResult.fieldErrors ?? (productResult.errorMessage ? [productResult.errorMessage] : [])
      )

    const errorMessages = results.flatMap(result => {
      if (result.status === 'rejected') {
        const body = /** @type {any} */ (result).reason?.response?.data ?? /** @type {any} */ (result).reason
        return extractErrors(body)
      }
      if (/** @type {any} */ (result).value?.isSuccess === false) {
        return extractErrors(/** @type {any} */ (result).value)
      }
      return []
    })

    const successMessages = results
      .filter(result => result.status === 'fulfilled' && /** @type {any} */ (result).value?.isSuccess !== false)
      .map(result => /** @type {any} */ (result).value?.message)
      .filter(Boolean)

    // A single error is shown in full; multiple errors collapse into a summary with expandable details.
    const notificationMessage =
      errorMessages.length > 0
        ? `${errorMessages.length} product updates failed.`
        : errorMessages[0] ||
          successMessages.join(' ') ||
          (hasFailure ? 'Bulk product edit failed.' : 'Products updated successfully.')

    dispatch(
      setToastNotifications({
        show: true,
        message: notificationMessage,
        details: errorMessages.length > 0 ? errorMessages : undefined,
        type: hasFailure ? 'error' : 'success',
      })
    )

    // Refetch the table so it reflects whatever the server actually persisted.
    await queryClient.invalidateQueries({queryKey: ['PRODUCT_LIST_V2']})

    setIsEditMode(false)
    setEditedValues({})
    onSaved?.()
  }, [
    editedValues,
    locale,
    selectedRows,
    onSaved,
    bulkEditMutateAsync,
    dispatch,
    queryClient,
    hasMissingRequiredFields,
    hasValidationErrors,
  ])

  return {
    isEditMode,
    editedValues,
    isSavingEdits,
    hasMissingRequiredFields,
    missingRequiredFieldColumns,
    hasValidationErrors,
    onEnterEditMode,
    onEditValueChange,
    onCancelEdit,
    onSaveEdit,
  }
}
